/**
 * usePushNotifications — React hook for FCM push notification management
 *
 * Handles:
 * - Checking browser support & current permission state
 * - Requesting permission + registering FCM token
 * - Listening for foreground messages (in-app toast)
 * - Cleanup on unmount
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  isPushSupported,
  getPermissionState,
  requestAndRegisterToken,
  listenForForegroundMessages,
} from '@/services/push-notification.service';

export interface ForegroundNotification {
  id: string;
  title: string;
  body: string;
  type: string;
  url: string;
  receivedAt: string;
}

export function usePushNotifications(userId: string | null) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [latestNotification, setLatestNotification] = useState<ForegroundNotification | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);

  // Check support on mount + poll permission state so the UI reacts
  // immediately when the user accepts/denies the browser prompt
  useEffect(() => {
    const isSupported = isPushSupported();
    setSupported(isSupported);
    if (isSupported) {
      setPermission(getPermissionState());
    }

    // Poll permission state every 1s while it's 'default' — catches the moment
    // the user responds to the browser prompt without needing a page refresh.
    // Stops polling once permission is decided (granted or denied).
    if (!isSupported) return;
    let stopped = false;
    const interval = setInterval(() => {
      if (stopped) return;
      const current = getPermissionState();
      setPermission(prev => {
        if (prev !== current) return current;
        return prev;
      });
      if (current !== 'default') {
        stopped = true;
        clearInterval(interval);
      }
    }, 1000);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, []);

  // Set up foreground message listener when we have a userId
  useEffect(() => {
    if (!userId || !supported) return;

    let cancelled = false;
    listenForForegroundMessages((payload) => {
      if (cancelled) return;
      setLatestNotification({
        id: crypto.randomUUID(),
        title: payload.title,
        body: payload.body,
        type: payload.type,
        url: payload.url,
        receivedAt: new Date().toISOString(),
      });
    }).then((unsub) => {
      if (!cancelled && unsub) {
        unsubRef.current = unsub;
      }
    });

    return () => {
      cancelled = true;
      unsubRef.current?.();
      unsubRef.current = null;
    };
  }, [userId, supported]);

  /**
   * Request notification permission and register FCM token.
   * Call this when the user taps "Enable Notifications".
   */
  const requestPermission = useCallback(async () => {
    if (!userId || !supported) return null;
    setLoading(true);
    try {
      const fcmToken = await requestAndRegisterToken(userId);
      setToken(fcmToken);
      setPermission(getPermissionState());
      return fcmToken;
    } finally {
      setLoading(false);
    }
  }, [userId, supported]);

  /**
   * Dismiss the latest foreground notification toast.
   */
  const dismissNotification = useCallback(() => {
    setLatestNotification(null);
  }, []);

  return {
    /** Whether push notifications are supported in this browser */
    supported,
    /** Current permission state: 'default' | 'granted' | 'denied' */
    permission,
    /** Whether we're currently requesting permission */
    loading,
    /** The FCM token (null until permission granted) */
    token,
    /** Most recent foreground notification (for in-app toast) */
    latestNotification,
    /** Request permission + register token */
    requestPermission,
    /** Dismiss the foreground notification toast */
    dismissNotification,
    /** Whether the user should be prompted (supported + not yet decided) */
    shouldPrompt: supported && permission === 'default',
    /** Whether notifications are active */
    isEnabled: supported && permission === 'granted',
  };
}
